import React, { useCallback, useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import RoundImageWithName from "../components/reusable/RoundImageWithName";
import {
  BG_STYLES,
  BORDER_STYLES,
  Button,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  LazyImage,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";

import ProgressBar from "../components/reusable/ProgressBar";
import WarningIcon from "../components/icons/warningIcon";
import NutrientSummary from "../components/NutrientSummary";
import SpraySlotAdvisory from "../components/SpraySlot";
import WeatherForecastCard from "../components/WeatherForecastCard";
import fetchAdvisory from "../apis/fetchAdvisory";
import MicroNutrientModal from "../components/MicroNutrientModal";
import {
  AdvisoryData,
  AdvisoryNames,
  CropGrowthAdvisoryResult,
  IrrigationAdvisoryResult,
  PathogenDetectionAdvisoryResult,
  SoilNutrientAdvisoryResult,
  SprayingAdvisoryResult,
  WeatherAdvisoryResult,
} from "../models/advistory";
import { DIMENSION_WIDTH } from "app/utils/common";
import ToastAndroid from "app/mocks/ToastAndroid";
import { AxiosError } from "axios";
import { AdvisoryScreenProps } from "../routes/types";
import { trackEvent } from "app/farmer360/utils/tracking";
import { CustomEventTypes } from "../utils/events/CustomEventTypes";
import User from "../models/user";
import { fetchUserDetails } from "../apis/fetchUserDetails";
import { useTranslation } from "react-i18next";
import PathogenDetectionCard from "../components/PathogenDetectionCard";

const Advisory = ({ route }: AdvisoryScreenProps) => {
  const { t } = useTranslation();
  const { advisoryId } = route.params || {};
  const [userDetails, setUserDetails] = useState<User | null>(null);
  const [advisoryData, setAdvisoryData] = useState<AdvisoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMicroNutrientModal, setShowMicroNutrientModal] = useState(false);

  useEffect(() => {
    const getUserDetails = async () => {
      let user: User | null;
      user = await fetchUserDetails();
      if (user) {
        setUserDetails(user);
      }
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    if (userDetails?.id && advisoryData) {
      trackEvent(CustomEventTypes.LandedOnAdvisoryPage, {
        subscriptionID: advisoryId,
        farmerName: userDetails?.full_name,
        farmerMobile: userDetails?.phone_number,
        AdvisoryType: "KhetEdge",
        CropName: advisoryData?.crop_name || "",
        DateOfSowing: advisoryData?.sowing_date,
        FarmerState: advisoryData?.state,
        FarmerDistrict: advisoryData?.district,
        FarmerBlock: "",
        FarmerVillage: "",
      });
    }
  }, [advisoryData, advisoryId, userDetails]);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAdvisory(advisoryId);
      setAdvisoryData(data);
    } catch (error) {
      if (error instanceof AxiosError) {
        const data = error?.response?.data;
        const errorMessage =
          data?.data_queries?.[0] ||
          data?.error?.message ||
          data?.message ||
          data?.detail ||
          error?.message;
        ToastAndroid.show(
          errorMessage || "Something went wrong",
          ToastAndroid.SHORT
        );
      } else {
        ToastAndroid.show("Something went wrong", ToastAndroid.SHORT);
      }
    } finally {
      setLoading(false);
    }
  }, [advisoryId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const cropGrowth = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.CropGrowth
  )?.result_data as CropGrowthAdvisoryResult;

  const weather = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.Weather
  )?.result_data as WeatherAdvisoryResult;

  const irrigation = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.Irrigation
  )?.result_data as IrrigationAdvisoryResult;

  const spraying = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.Spraying
  )?.result_data as SprayingAdvisoryResult;

  const soilNutrients = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.SoilNutrients
  )?.result_data as SoilNutrientAdvisoryResult;

  const pathogenDetection = advisoryData?.advisories?.find(
    (adv) => adv.advisory_name === AdvisoryNames.PathogenDetection
  )?.result_data as PathogenDetectionAdvisoryResult;

  const hazard = weather?.hazard;
  const todayWeather = weather?.today;
  const hourlyWeather = weather?.hourly;
  const weeklyWeather = weather?.weekly;

  const isTab = DIMENSION_WIDTH > 425;

  const location = [advisoryData?.district, advisoryData?.state]
    .filter(Boolean)
    .join(", ");
  if (loading || !advisoryData) {
    return <Spinner />;
  }
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        overflow: "scroll",
        width: "100%",
      }}
    >
      <View style={{ maxWidth: 600, width: "100%" }}>
        {advisoryData.crop_name && (
          <View
            style={[
              styles.section,
              COMMON_STYLES.row,
              COMMON_STYLES.justifyBetween,
            ]}
          >
            <View>
              <Text
                style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
              >
                {advisoryData?.crop_name}
              </Text>
              {advisoryData?.crop_variety ? (
                <Text style={styles.text}>
                  {t("variety", { name: advisoryData?.crop_variety })}
                </Text>
              ) : null}
              <Text style={styles.text}>
                {advisoryData?.transplantation_date
                  ? t("transplantation_date", {
                      date: advisoryData?.transplantation_date,
                    })
                  : t("sowing_date", { date: advisoryData?.sowing_date })}
              </Text>
            </View>
            <View>
              <RoundImageWithName
                name={""}
                imgUrl={advisoryData?.crop_image}
                handleItemClick={() => {}}
                key={12}
                imageStyle={[
                  BORDER_STYLES.borderNeutral10,
                  COMMON_STYLES.b1,
                  styles.img50X50,
                ]}
                textStyle={[
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral100,
                ]}
                textNumberOfLines={2}
                style={[COMMON_STYLES.mr1]}
              />
            </View>
          </View>
        )}
        {cropGrowth && (
          <View style={[BG_STYLES.bgWhite, COMMON_STYLES["mb1/2"]]}>
            <View style={[COMMON_STYLES.p1]}>
              <Text
                style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
              >
                {t("crop_growth")}
              </Text>
              <View style={[COMMON_STYLES.row]}>
                <Text style={[styles.text, { flex: 0.6 }]}>
                  {t("current_crop_stage")}:
                </Text>
                <Text style={[styles.text, { flex: 0.4 }]}>
                  {cropGrowth?.stages?.[cropGrowth?.current_stage]}
                </Text>
              </View>
              <View style={[COMMON_STYLES.row, COMMON_STYLES.justifyBetween]}>
                <Text style={[styles.text, { flex: 0.6 }]}>
                  {t("growing_degree_days")}:
                </Text>
                <Text style={[styles.text, { flex: 0.4 }]}>
                  {cropGrowth?.growing_degree_days}
                </Text>
              </View>
              <ProgressBar
                value={cropGrowth?.growth_percent || 0}
                style={COMMON_STYLES["mt1/2"]}
                fillColor="#43A314"
              />
            </View>

            <LazyImage
              imageUrl={cropGrowth?.advisory_image}
              imgStyles={[
                isTab ? styles.bannerImgDesktop : styles.bannerImg,
                { borderRadius: 0, ...BG_STYLES.bgWhite },
              ]}
              isResizeMethod
            />
            {/* <View style={[COMMON_STYLES.p1]}>
              <Button
                text="View Crop Calendar"
                type={BUTTON_TYPE.SECONDARY}
                onClick={() => {}}
              />
            </View> */}
          </View>
        )}
        {hazard && (
          <View style={[styles.section]}>
            <Text
              style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
            >
              {t("weather")}
            </Text>
            <LazyImage
              imageUrl={hazard?.hazard_image}
              imgStyles={[isTab ? styles.bannerImgDesktop : styles.bannerImg]}
            />
            <View
              style={[
                BG_STYLES.bgYellow10,
                COMMON_STYLES.p1,
                COMMON_STYLES["ph3/2"],
                COMMON_STYLES.br1,
                { borderColor: COLORS.yellow20, borderWidth: 1 },
              ]}
            >
              <View style={[COMMON_STYLES.row, COMMON_STYLES.alignItemsCenter]}>
                <LazyImage
                  imageUrl={hazard?.alert?.icon}
                  imgStyles={{ width: 30, height: 27, marginRight: 6 }}
                  isResizeMethod
                />
                <Text
                  style={[
                    COMMON_STYLES["ml1/4"],
                    TYPOGRAPHY.CAPTION1,
                    VARIANTS_STYLES.MEDIUM,
                  ]}
                >
                  <Text style={[VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                    {hazard?.alert.type} Alert:{" "}
                  </Text>
                  {hazard?.alert?.line}
                </Text>
              </View>

              <View style={[COMMON_STYLES.row, COMMON_STYLES["mt1/2"]]}>
                <View style={[{ flex: 0.6 }, COMMON_STYLES["mt1"]]}>
                  <Text
                    style={[
                      TYPOGRAPHY.HEADING3,
                      VARIANTS_STYLES.SEMI_EMPHASIZED,
                      COMMON_STYLES["mb1/4"],
                    ]}
                  >
                    {hazard?.weather_type}
                  </Text>
                  <Text style={[TYPOGRAPHY.CAPTION1]}>
                    {hazard?.weather_until}
                  </Text>
                  {/* <Text style={[TYPOGRAPHY.CAPTION1]}>
                    {hazard?.weather_sub}
                  </Text> */}
                  <Text style={[styles.text, TYPOGRAPHY.CAPTION2]}>
                    {location}
                  </Text>
                </View>
                <View style={[{ flex: 0.4 }, COMMON_STYLES.alignItemsCenter]}>
                  <LazyImage
                    imageUrl={hazard?.hazard_icon}
                    imgStyles={[styles.weatherIcon]}
                  />
                </View>
              </View>
            </View>
          </View>
        )}
        {todayWeather && (
          <View style={[styles.section]}>
            <View>
              <Text
                style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
              >
                {t("today_weather")}
              </Text>
              <Text style={[TYPOGRAPHY.CAPTION2, { color: COLORS.neutral90 }]}>
                {location}
              </Text>
            </View>

            <WeatherForecastCard
              data={todayWeather}
              hourlyWeather={hourlyWeather}
              weaklyWeather={weeklyWeather}
              location={location}
            />
          </View>
        )}
        {irrigation && (
          <View style={[styles.section]}>
            <Text
              style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
            >
              {t("irrigation")}
            </Text>
            <LazyImage
              imageUrl={irrigation?.advisory_image}
              imgStyles={[isTab ? styles.bannerImgDesktop : styles.bannerImg]}
            />

            <View
              style={[
                BG_STYLES.bgYellow10,
                COMMON_STYLES.p1,
                COMMON_STYLES.br1,
                COMMON_STYLES.row,
                COMMON_STYLES.alignItemsCenter,
                { borderColor: COLORS.yellow20, borderWidth: 1 },
              ]}
            >
              {irrigation?.alert_sub ? (
                <WarningIcon
                  width={40}
                  height={40}
                  style={[COMMON_STYLES.mr1]}
                />
              ) : null}
              <View>
                <Text
                  style={[
                    COMMON_STYLES["ml1/4"],
                    TYPOGRAPHY.TEXT1,
                    VARIANTS_STYLES.SEMI_EMPHASIZED,
                  ]}
                >
                  {irrigation?.alert}
                </Text>
                {irrigation?.alert_sub ? (
                  <Text
                    style={[
                      COMMON_STYLES["ml1/4"],
                      TYPOGRAPHY.CAPTION1,
                      VARIANTS_STYLES.MEDIUM,
                    ]}
                  >
                    {irrigation?.alert_sub}
                  </Text>
                ) : null}
              </View>
            </View>
            {irrigation?.best_time ? (
              <View style={styles.alertSub}>
                <View style={[COMMON_STYLES.row]}>
                  <LazyImage
                    imageUrl={irrigation?.weather?.icon}
                    imgStyles={styles.icon}
                  />
                  <Text style={[COMMON_STYLES["ml1"], VARIANTS_STYLES.MEDIUM]}>
                    {t("best_time_to_irrigate")} :{" "}
                    <Text style={[VARIANTS_STYLES.SEMI_EMPHASIZED]}>
                      {irrigation?.best_time}
                    </Text>
                  </Text>
                </View>
                <View style={[COMMON_STYLES.row, COMMON_STYLES["mt1/2"]]}>
                  <LazyImage
                    imageUrl={irrigation?.weather?.icon}
                    imgStyles={styles.icon}
                  />
                  <View style={[COMMON_STYLES["ml1"]]}>
                    <Text style={[VARIANTS_STYLES.MEDIUM]}>
                      {irrigation?.weather?.weather_line}
                    </Text>
                    <Text style={[VARIANTS_STYLES.MEDIUM]}>
                      {irrigation?.weather?.weather_sub}
                    </Text>
                  </View>
                </View>
              </View>
            ) : null}
          </View>
        )}
        {spraying && (spraying.today || spraying.tomorrow) && (
          <View style={[styles.section]}>
            <Text
              style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
            >
              {t("best_time_to_spraying")}
            </Text>
            <LazyImage
              imageUrl={spraying?.advisory_image}
              imgStyles={[isTab ? styles.bannerImgDesktop : styles.bannerImg]}
            />
            <SpraySlotAdvisory data={spraying} />
          </View>
        )}
        {soilNutrients && !soilNutrients?.error && (
          <View style={[styles.section]}>
            <View>
              <Text
                style={[TYPOGRAPHY.HEADING3, VARIANTS_STYLES.SEMI_EMPHASIZED]}
              >
                {t("soil_nutrients_your_area")}
              </Text>
              <Text style={[TYPOGRAPHY.CAPTION2, { color: COLORS.neutral90 }]}>
                {location}
              </Text>
            </View>

            <LazyImage
              imageUrl={soilNutrients?.advisory_image}
              imgStyles={[isTab ? styles.bannerImgDesktop : styles.bannerImg]}
              isResizeMethod
            />
            <Text>
              {t("nutrients_of_soil_in", { location: advisoryData?.district })}
            </Text>
            <View>
              <NutrientSummary data={soilNutrients?.nutrients} />
              <View style={styles.buttonWrapper}>
                <Button
                  text={t("view_micro_nutrients")}
                  type={BUTTON_TYPE.SECONDARY}
                  onClick={() => {
                    setShowMicroNutrientModal(true);
                  }}
                  size={SIZE.large}
                />
              </View>
            </View>
            <MicroNutrientModal
              visible={showMicroNutrientModal}
              onClose={() => setShowMicroNutrientModal(false)}
              data={soilNutrients?.micronutrients}
              imageUrl={soilNutrients?.micronutrients_image}
              title={t("soil_micro-nutrients_your_area")}
              subTitle={location}
              district={advisoryData?.district}
              crop={advisoryData?.crop_name}
            />
          </View>
        )}
        {pathogenDetection && (
          <PathogenDetectionCard pathogenData={pathogenDetection} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  text: {
    color: COLORS.neutral90,
    ...TYPOGRAPHY.TEXT2,
    marginTop: 6,
  },
  img50X50: {
    height: 50,
    width: 50,
    borderRadius: 50,
  },
  section: {
    display: "flex",
    gap: 10,
    padding: 16,
    backgroundColor: COLORS.white,
    marginBottom: 8,
  },
  bannerImg: {
    width: "100%",
    height: 220,
    maxWidth: 600,
    borderRadius: 16,
    margin: "auto",
    resizeMode: "contain",
  },
  bannerImgDesktop: {
    width: "100%",
    height: 350,
    maxWidth: 600,
    borderRadius: 16,
    margin: "auto",
    resizeMode: "contain",
  },
  weatherIcon: {
    width: 100,
    height: 100,
  },
  activeStage: {
    backgroundColor: "#43A314",
    color: COLORS.white,
  },
  buttonWrapper: {
    marginTop: 8,
    paddingHorizontal: 12,
  },
  alertSub: {
    display: "flex",
    backgroundColor: "#F5F8FA",
    padding: 24,
    borderRadius: 16,
  },
  icon: {
    width: 20,
    height: 20,
  },
});

export default Advisory;
